/**
 * Feld-Liste des Channels-Editors (C1/C2) — Single- oder Side-by-Side-Modus.
 *
 * Single: gestapelte ContentBlockFieldCards. Side-by-Side: pro Feld zwei Spalten
 * [editierbare Karte | read-only Vergleichs-Panel des „Vergleiche mit"-Ziels].
 */

import { Fragment } from 'react'
import { ContentBlockFieldCard } from './ContentBlockFieldCard'
import { ContentComparePanel } from './ContentComparePanel'
import { maxLengthFor, restrictionNoteFor } from './channelScope'
import type { MergedField } from './contentInheritance'
import type { BlockType, ContentBlock } from '@/types/content-blocks'
import type { ChannelPackage } from '@/types/channel-packages'

export interface CompareValue {
  name: string
  value: string
  badge: 'master' | 'override' | 'inherited'
}

interface Props {
  fields: MergedField[]
  lang: string
  productId: string
  selectedChannelId: string
  isProductScope: boolean
  selectedPackage?: ChannelPackage
  isJTLChannel: boolean
  sideBySide: boolean
  resolveTarget: (blockType: BlockType) => CompareValue
  highlightBlockType?: BlockType | null
  /** Master-Wert je Feldtyp (für die Master-Ribbon bei Abweichungen) */
  masterValueFor: (blockType: BlockType) => string | undefined
  /** Channel-UUID für den KI-Text-Button (undefined = Produkt-Scope) */
  aiChannelId?: string
  onCompare: (block: ContentBlock) => void
}

export function ChannelFieldsList({
  fields,
  lang,
  productId,
  selectedChannelId,
  isProductScope,
  selectedPackage,
  isJTLChannel,
  sideBySide,
  resolveTarget,
  highlightBlockType,
  masterValueFor,
  aiChannelId,
  onCompare,
}: Props) {
  return (
    <>
      {fields.map((field) => {
        const card = (
          <ContentBlockFieldCard
            block={field.block}
            lang={lang}
            maxLength={maxLengthFor(field.blockType, selectedPackage)}
            productId={productId}
            channelPackageId={isProductScope ? null : selectedChannelId}
            channelId={aiChannelId}
            isJTLChannel={isJTLChannel}
            deviation={isProductScope || !field.block.id ? null : field.inherited ? 'inherited' : 'override'}
            highlighted={highlightBlockType === field.blockType}
            masterValue={field.inherited ? undefined : masterValueFor(field.blockType)}
            restrictionNote={restrictionNoteFor(field.blockType)}
            onCompare={() => onCompare(field.block)}
          />
        )
        if (!sideBySide) return <Fragment key={field.blockType}>{card}</Fragment>
        const target = resolveTarget(field.blockType)
        return (
          <div key={field.blockType} className="grid grid-cols-2 gap-3 items-start">
            {card}
            <ContentComparePanel targetName={target.name} value={target.value} badge={target.badge} />
          </div>
        )
      })}
    </>
  )
}
